import { Store } from '@ngrx/store';

import { ProductMapPrices } from '../models/product-prices.model';
import { configEnableTriplePrice$, configTriplePricePercent$, configEnablePercent$, configTypePercent$ } from '../services/product-prices.selectors';

import { getDiscountsPercentages } from './get-discount-percentages.use-case';
import { getLastReducedPriceUseCase, getOldPriceUseCase, getOriginalPriceUseCase, getPercentUseCase, getPriceUseCase, getUnitPriceUseCase } from './get-price-type.use-cases';

import { ProductPublicModels } from '@features/products/product';
import { getPeriod, getUserEventIsSales$$ } from '@features/user-events';

export function getPricesSyncUseCase(store: Store, product: ProductPublicModels.PublicProduct): ProductMapPrices {
    return calculatePricesUseCase(
        product,
        store.selectSignal(getPeriod)(),
        getUserEventIsSales$$(store)(),
        store.selectSignal(configEnableTriplePrice$)(),
        store.selectSignal(configTriplePricePercent$)(),
        store.selectSignal(configEnablePercent$)(),
        store.selectSignal(configTypePercent$)(),
    );
}

export function getPricesProductSyncUseCase(store: Store, product: ProductPublicModels.PublicProduct): ProductMapPrices {
    const priceInfoView = getPricesSyncUseCase(store, product);
    const urlLinkFinded = product.attributes.find(attribute => attribute.name.includes('URLLINK'));
    if (!urlLinkFinded) {
        return priceInfoView;
    }
    return priceRangeLinkUseCase(product, priceInfoView);
}

export function priceRangeLinkUseCase(
    product: ProductPublicModels.PublicProduct,
    priceInfoView: ProductMapPrices
): ProductMapPrices {
    const sizes = product.colors[0].sizes;
    const rangeValue = `${sizes[0].priceInfo.priceWithCurrencySymbol.toString()}-${sizes[1].priceInfo.priceWithCurrencySymbol.toString()}`;
    return {
        ...priceInfoView,
        price: {
            ...priceInfoView.price,
            value: rangeValue as any, // Change other value -> rangeValue as string but value its number
        },
    };
}

/**
     * Calculates prices and percentages for a given product.
     *
     * @param product - The product for which prices are being calculated.
     * @param period - The period for which prices should be calculated ("DEFAULT", "PREWARM", "VEA" or "SALES").
     * @param showPrivateSale - Indicates whether private sale should be enabled.
     * @param configEnableTriplePrice - A configuration option to enable or disable triple pricing.
     * @param configTriplePricePercent - A configuration option for the percentage used in triple pricing.
     *      - "disabled": The percentage is not shown.
     *      - "original": The percentage is shown and calculated based on the first price (season price).
     *      - "cut": The percentage is shown and calculated based on the second price (last reduced price).
     * @param configShowPercent - Indicates whether the percentage value should be displayed.
     * @param configTypePercent - A configuration option for the type of percentage calculation.
     *      - 0: Rounded to the nearest multiple of 5.
     *      - 1: Rounded to the nearest whole number.
     * @returns An object containing the calculated prices and percentage.
    */
export function calculatePricesUseCase(
    product: ProductPublicModels.PublicProduct,
    period: string,
    showPrivateSale: boolean,
    configEnableTriplePrice: boolean,
    configTriplePricePercent: string,
    configShowPercent: boolean,
    configTypePercent: number,
): ProductMapPrices {
    const price = getPriceUseCase(product.priceInfo, period, showPrivateSale); // primary price
    const oldPrice = getOldPriceUseCase(product.priceInfo, price, showPrivateSale); // secondary price
    const originalPrice = getOriginalPriceUseCase(product.priceInfo, oldPrice, configEnableTriplePrice); // terciary price
    const lastReducedPrice = getLastReducedPriceUseCase(product.priceInfo, oldPrice, configEnableTriplePrice);
    const unitPrice = getUnitPriceUseCase(product.priceInfo);
    const mapPrices = {
        price, oldPrice, originalPrice, lastReducedPrice,
    };
    const mapConfigs = {
        configEnableTriplePrice, configTriplePricePercent, configShowPercent, configTypePercent,
    };

    const discountsPercentages = getDiscountsPercentages(product.colors, product?.status?.selectedColor?.id);
    const percent = getPercentUseCase(mapPrices, mapConfigs, discountsPercentages);
    const productMapPrices = {
        price, oldPrice, lastReducedPrice, originalPrice, unitPrice, percent,
    };

    return productMapPrices;
}
